import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { ChartContainer } from './styles';

import {
    APPROVAL_STAGE_COLORS,
    APPROVAL_STAGE_RADII,
    CHART_DIMENSIONS,
    COLORS,
    SEGMENT_TYPE,
} from '../../../../constants/radar'

const RadarChart = ({
  data,
  selectedSubcategory,
  zoomFactor,
  //panPosition,
  isDragging,
  hoveredItem,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onHover,
  onMouseOut,
  onSelection,
  onBack
}) => {

  const svgRef = useRef(null);
  const width = CHART_DIMENSIONS.WIDTH;
  const height = CHART_DIMENSIONS.HEIGHT;

  const [selectedLeafItem, setSelectedLeafItem] = useState(null);

  // Constants for capacity calculation
  const MIN_ITEM_ARC_LENGTH = 50; // Minimum pixels between items to prevent overlap
  const TEXT_SIZE = 14; // Font size in pixels

  const calculateCapacity = useCallback((radius) => {
    const circumference = 2 * Math.PI * radius;
    return Math.floor(circumference / MIN_ITEM_ARC_LENGTH);
  }, []);

  const distributeCapacity = useCallback((categories, totalCapacity) => {
    const totalSubcategories = categories.reduce((sum, category) => 
      sum + (category.subcategories?.length || 1), 0);
    
    return categories.map(category => {
      const subcatCount = category.subcategories?.length || 1;
      const proportion = subcatCount / totalSubcategories;
      const allocatedSlots = Math.max(1, Math.floor(totalCapacity * proportion));
      
      return {
        ...category,
        allocatedSlots,
        hiddenCount: Math.max(0, subcatCount - allocatedSlots)
      };
    });
  }, []);

    const isLeafItem = (item) => {
        return item && item.segmentType === SEGMENT_TYPE.LEAF;
    }

    const isLeafLevel = (subcategories) => {
        return subcategories?.length > 0 && subcategories.every(item => isLeafItem(item));
    };

    const approvalStages = [
      { name: 'ADOPT', radius: APPROVAL_STAGE_RADII['ADOPT'], color: COLORS.BACKGROUND_DARK },    // Darker gray from image
      { name: 'TRIAL', radius: APPROVAL_STAGE_RADII['TRIAL'], color: COLORS.BACKGROUND_DARK },  // Darker gray from image
      { name: 'ASSESS', radius: APPROVAL_STAGE_RADII['ASSESS'], color: COLORS.BACKGROUND_DARK },   // Darker gray from image
      { name: 'HOLD', radius: APPROVAL_STAGE_RADII['HOLD'], color: COLORS.BACKGROUND_DARK }    // Darker gray from image
    ];


      // function to draw the arrow near the category
      const drawArrow = (group, startX, startY, endX, endY) => {
        const arrowSize = 10; // Size of the arrowhead

        const angle = Math.atan2(endY - startY, endX - startX) + Math.PI / 2; // Adjust angle for clockwise
        const arrowX = endX + (arrowSize / 2) * Math.cos(angle);
        const arrowY = endY + (arrowSize / 2) * Math.sin(angle);
 
  
        group.append('path')
        .attr('d', `M${arrowX},${arrowY} 
                  L${arrowX - arrowSize * Math.cos(angle + Math.PI / 6)},${arrowY - arrowSize * Math.sin(angle + Math.PI / 6)} 
                  L${arrowX - arrowSize * Math.cos(angle - Math.PI / 6)},${arrowY - arrowSize * Math.sin(angle - Math.PI / 6)} 
                  Z`)
        .style('fill', COLORS.TEXT);
    };

  const renderCircles = useCallback((group, rScale, approvalStages, isAtLeafLevel) => {
    // First, draw the outer background circle with lighter color
    group
      .append('circle')
      .attr('r', rScale(8))  // Slightly larger than HOLD radius
      .style('fill', 'none')
      .style('stroke', COLORS.BACKGROUND)  // Lighter gray from image
      .style('stroke-width', '8px');

    // Draw inner background circle
    group
      .append('circle')
      .attr('r', rScale(1.5))
      .style('fill', 'none')
      .style('stroke', COLORS.BACKGROUND)  // Lighter gray from image
      .style('stroke-width', '8px');

    // Draw background circles
    approvalStages.forEach(r => {
      group
        .append('circle')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', rScale(r.radius))
        .style('fill', 'none')
        .style('stroke', r.color)
        .style('stroke-width', '1px');
        
        if (isAtLeafLevel) {
            group
                .append('text')
                .attr('x', 0)
                .attr('y', -rScale(r.radius) + 20)
                .attr('text-anchor', 'middle')
                .style('fill', APPROVAL_STAGE_COLORS[r.name.toUpperCase()])  // Original colors for text
                .style('opacity', 0.6)
                .style('font-size', '14px')
                .style('font-weight', 'bold')
                .text(r.name);
        }

    });
  }, []);

  const renderBackButton = useCallback((group, rScale, bEvent) => {
          // Create a group for the back button
        const backButtonGroup = group
          .append('g')
          .style('cursor', 'pointer')
          .on('click', (event) => {
            event.stopPropagation();
            if (bEvent) {
              bEvent();
            }
          });
  
        // Add circular background to the group
        backButtonGroup
          .append('circle')
          .attr('r', rScale(1))
          .style('fill', '#EBEEF2');
  
        // Add the back arrow icon to the same group
        backButtonGroup
          .append('path')
          .attr('d', `M${rScale(0.3)},0 
                      L${rScale(-0.3)},0 
                      M${rScale(-0.3)},0 
                      L${rScale(-0.1)},${rScale(-0.2)} 
                      M${rScale(-0.3)},0 
                      L${rScale(-0.1)},${rScale(0.2)}`)
          .style('fill', 'none')
          .style('stroke', '#2B4B81')
          .style('stroke-width', '2.5px')
          .style('stroke-linecap', 'round')
          .style('stroke-linejoin', 'round');
  
        // Add hover effects to the group
        backButtonGroup
          .on('mouseover', function() {
            d3.select(this)
              .select('path')
              .style('stroke', '#1a3366')
              .style('stroke-width', '3px');
          })
          .on('mouseout', function() {
            d3.select(this)
              .select('path')
              .style('stroke', '#2B4B81')
              .style('stroke-width', '2.5px');
          });
  }, []);

  const drawLineAndDot = useCallback((group, {
      item,
      rScale,
      subcatAngle,
      onHover,
      onMouseOut,
  }) => {

        const TEXT_PADDING = 10;
        const subcatDistance = rScale(8.8);
        const stageRadius = rScale(APPROVAL_STAGE_RADII[item.approvalStage.toUpperCase()]);

        const dotCoords = {
          x: stageRadius * Math.cos(subcatAngle - Math.PI / 2),
          y: stageRadius * Math.sin(subcatAngle - Math.PI / 2)
        };


        const lineEndCoords = {
          x: (subcatDistance - TEXT_PADDING) * Math.cos(subcatAngle - Math.PI / 2),
          y: (subcatDistance - TEXT_PADDING) * Math.sin(subcatAngle - Math.PI / 2)
        };

        // Add connecting line
        group
          .append('line')
          .attr('x1', dotCoords.x)
          .attr('y1', dotCoords.y)
          .attr('x2', lineEndCoords.x)
          .attr('y2', lineEndCoords.y)
          .style('stroke', APPROVAL_STAGE_COLORS[item.approvalStage.toUpperCase()])
          .style('stroke-width', '1px')
          .style('stroke-dasharray', '2,2')
          .style('opacity', 0.6);

        // Add dot
        group
          .append('circle')
          .attr('cx', dotCoords.x)
          .attr('cy', dotCoords.y)
          .attr('r', 4)
          .attr('data-item-id', item.id)
          .style('fill', APPROVAL_STAGE_COLORS[item.approvalStage.toUpperCase()])
          .style('cursor', 'pointer')
          .on('mouseover', (e) => onHover(item, false, e))
          .on('mouseout', onMouseOut);
  }, []);


  const drawText = useCallback((group, {
      item,
      position,
      color,
      currentAngle,
      fontFunction,
      textFunction,
      onHover,
      onMouseOut,
      onClick,
  }) => {
    const rotationAngle = ((currentAngle * 180) / Math.PI - 90);
      
    const text = group
      .append('text')
      .attr('x', position.x)
      .attr('y', position.y)
      .attr('transform', `
        translate(${position.x}, ${position.y})
        rotate(${rotationAngle})
        ${currentAngle > Math.PI ? 'rotate(180)' : ''}
        translate(${-position.x}, ${-position.y})
      `)
      .style('text-anchor', currentAngle > Math.PI ? 'end' : 'start')
      .attr("dy", ".35em")
      .style('font-size', '14px')
      .style('fill', color)
      .style('font-weight', fontFunction)
      .style('cursor', onClick ? 'pointer' : 'default')
      .text(textFunction);

    // Add event listeners only if they exist
    if (onHover) {
      text.on('mouseover', function(event) {
        d3.select(this)
          .transition()
          .duration(200)
          .style('font-weight', 'bold');
        onHover(item, false, event);
      });
    }
    if (onMouseOut) {
      text.on('mouseout', function() {
        d3.select(this)
          .transition()
          .duration(200)
          .style('font-weight', fontFunction);
        onMouseOut();
      });
    }
    if (onClick) {
      text.on('click', function(event) {
        event.stopPropagation();
        onClick(item);
      });
    }
  }, []);

  const findItemsWithSharedTags = useCallback((items) => {
    const connections = [];
    
    // Create a flat array of leaf items
    const leafItems = items.filter(item => isLeafItem(item));
    
    // Find tags that are common to all leaf items
    const commonTags = new Set(
      leafItems[0]?.tags?.filter(tag => 
        leafItems.every(item => 
          item.tags?.some(itemTag => itemTag.id === tag.id)
        )
      )?.map(tag => tag.id) || []
    );

    // Create a map of unique tags to colors
    const tagColors = new Map();
    const uniqueTags = new Set();
    
    // Collect all unique leaf tags (excluding common and root tags)
    leafItems.forEach(item => {
      item.tags?.forEach(tag => {
        // Only include tags that:
        // 1. Are not common to all items
        // 2. Are not root tags (have a parentTagId)
        // 3. Have not been added yet
        if (!commonTags.has(tag.id) && tag.parentTagId && !uniqueTags.has(tag.id)) {
          uniqueTags.add(tag.id);
        }
      });
    });

    // Custom color palette for tags - using highly distinct colors
    const tagColorPalette = [
      '#E63946', // vivid red
      '#2A9D8F', // teal
      '#6A4C93', // deep purple
      '#F4A261', // sandy orange
      '#1D3557', // navy blue
      '#F7B801', // golden yellow
      '#06D6A0', // bright mint
      '#BC4749', // burgundy
      '#4361EE', // electric blue
      '#9D4EDD'  // bright purple
    ];

    // Assign colors to tags
    Array.from(uniqueTags).forEach((tagId, index) => {
      tagColors.set(tagId, tagColorPalette[index % tagColorPalette.length]);
    });
    
    // Compare each pair of items
    for (let i = 0; i < leafItems.length; i++) {
      for (let j = i + 1; j < leafItems.length; j++) {
        const item1 = leafItems[i];
        const item2 = leafItems[j];
        
        // Find shared tags, excluding common and root tags
        const sharedTags = item1.tags?.filter(tag1 => 
          !commonTags.has(tag1.id) && // Exclude common tags
          tag1.parentTagId && // Exclude root tags
          item2.tags?.some(tag2 => tag2.id === tag1.id)
        ) || [];
        
        if (sharedTags.length > 0) {
          connections.push({
            source: item1,
            target: item2,
            sharedTags,
            color: tagColors.get(sharedTags[0].id) // Use color of first shared tag
          });
        }
      }
    }
    
    return connections;
  }, []);

  const drawChordConnection = useCallback((group, {
    source,
    target,
    sourceAngle,
    targetAngle,
    rScale,
    sharedTags,
    color
  }) => {
    const radius = rScale(8); // Keep connections at outer circle
    const sourceX = radius * Math.cos(sourceAngle - Math.PI / 2);
    const sourceY = radius * Math.sin(sourceAngle - Math.PI / 2);
    
    const targetX = radius * Math.cos(targetAngle - Math.PI / 2);
    const targetY = radius * Math.sin(targetAngle - Math.PI / 2);

    // Calculate control points for a more pronounced curve
    const midX = (sourceX + targetX) / 2;
    const midY = (sourceY + targetY) / 2;
    
    // Calculate the angle between points to adjust curve intensity
    const angleDiff = Math.abs(targetAngle - sourceAngle);
    const curveIntensity = Math.min(1.2, Math.max(0.6, angleDiff / Math.PI));
    
    // Move control point towards center based on angle difference
    const controlRadius = radius * (1 - curveIntensity * 0.4); // Pull curve inward
    const controlAngle = (sourceAngle + targetAngle) / 2 - Math.PI / 2;
    const controlX = controlRadius * Math.cos(controlAngle);
    const controlY = controlRadius * Math.sin(controlAngle);

    // Create a group for the connection and label
    const connectionGroup = group.append('g');

    // Draw the curved connection
    const path = connectionGroup
      .append('path')
      .attr('d', `M ${sourceX},${sourceY} Q ${controlX},${controlY} ${targetX},${targetY}`)
      .style('fill', 'none')
      .style('stroke', color)
      .style('stroke-width', '1.5px')
      .style('stroke-dasharray', '4,4')
      .style('opacity', 0.5)
      .style('pointer-events', 'none');

    // Calculate text position with offset based on curve direction
    const textAngle = Math.atan2(targetY - sourceY, targetX - sourceX);
    const textOffsetDistance = 15; // Distance to offset the text from the control point
    const textX = controlX + Math.sin(textAngle) * textOffsetDistance;
    const textY = controlY - Math.cos(textAngle) * textOffsetDistance;

    // Create a text element for the shared tags
    const tagText = connectionGroup
      .append('text')
      .attr('x', textX)
      .attr('y', textY)
      .attr('text-anchor', 'middle')
      .attr('dy', -5)
      .style('fill', color)
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('opacity', 0)
      .text(sharedTags.map(tag => tag.name).join(', '));

    // Add a white background for better readability
    const textBBox = tagText.node().getBBox();
    const padding = 4;
    const textBackground = connectionGroup
      .insert('rect', 'text')
      .attr('x', textBBox.x - padding)
      .attr('y', textBBox.y - padding)
      .attr('width', textBBox.width + 2 * padding)
      .attr('height', textBBox.height + 2 * padding)
      .attr('rx', 4)
      .style('fill', 'white')
      .style('opacity', 0);

    // Create a wider invisible path for better hover detection
    const hitArea = connectionGroup
      .append('path')
      .attr('d', `M ${sourceX},${sourceY} Q ${controlX},${controlY} ${targetX},${targetY}`)
      .style('fill', 'none')
      .style('stroke', 'transparent')
      .style('stroke-width', '15px') // Wider area for hover detection
      .style('pointer-events', 'all')
      .style('cursor', 'pointer');

    const highlightConnection = () => {
      path.style('opacity', 0.9)
          .style('stroke-width', '2.5px');
      tagText.style('opacity', 1);
      textBackground.style('opacity', 0.95);
    };

    const unhighlightConnection = () => {
      path.style('opacity', 0.5)
          .style('stroke-width', '1.5px');
      tagText.style('opacity', 0);
      textBackground.style('opacity', 0);
    };

    // Add hover events only to the hit area
    hitArea
      .on('mouseover', highlightConnection)
      .on('mouseout', unhighlightConnection);

    // Add hover events for the source and target items
    const sourceSelector = `[data-item-id="${source.id}"]`;
    const targetSelector = `[data-item-id="${target.id}"]`;
    
    group.selectAll(sourceSelector)
      .on('mouseover.chord', highlightConnection)
      .on('mouseout.chord', unhighlightConnection);

    group.selectAll(targetSelector)
      .on('mouseover.chord', highlightConnection)
      .on('mouseout.chord', unhighlightConnection);
  }, []);

  //main
  useEffect(() => {

    if (!data || !svgRef.current) return;

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', '-500 -500 1000 1000')
      .attr('preserveAspectRatio', 'xMidYMid meet');

    svg.selectAll('*').remove();

    // Create main group with proper positioning
    const mainGroup = svg
      .append('g')
      .attr('transform', `translate(0,0) scale(${zoomFactor})`);

    // Get current data
    const currentData = selectedSubcategory ? selectedSubcategory : data
    const isAtLeafLevel = isLeafLevel(currentData.subcategories);
    const rScale = d3.scaleLinear().domain([0, 10]).range([0, 300]);
      
    // Calculate capacity based on the outer circle's radius
    const labelRadius = isAtLeafLevel ? rScale(9) : rScale(9.5);
    const totalCapacity = calculateCapacity(labelRadius);
    
    // Distribute capacity among categories
    const processedData = distributeCapacity(
      currentData.subcategories || [], 
      totalCapacity
    );

    // Create separate groups for different layers
    const circlesGroup = mainGroup.append('g').attr('class', 'circles-layer');
    const itemsGroup = mainGroup.append('g').attr('class', 'items-layer');
    const connectionsGroup = mainGroup.append('g').attr('class', 'connections-layer');
    const backButtonGroup = mainGroup.append('g').attr('class', 'back-button-layer');

    // Render circles in the bottom layer
    renderCircles(circlesGroup, rScale, approvalStages, isAtLeafLevel || isLeafItem(currentData));

    // Store angles for each leaf item for chord connections
    const leafItemAngles = new Map();

    // Initialize angle calculations
    let currentAngle = 0;
    const totalAllocatedSlots = processedData.reduce((sum, item) => sum + item.allocatedSlots, 0);
    const angleSlice = (2 * Math.PI) / totalAllocatedSlots;

    if (currentData.segmentType === SEGMENT_TYPE.LEAF) {
        drawLineAndDot(itemsGroup, {
            item: currentData,
            rScale,
             subcatAngle: 0,
             onHover,
             onMouseOut,
        });
  
        const labelRadius = rScale(9);
        drawText(itemsGroup, {
            item: currentData,
            position: {
                x: labelRadius * Math.cos(0 - Math.PI / 2),
                y: labelRadius * Math.sin(0 - Math.PI / 2)
            },
            color: COLORS.PRIMARY,
            currentAngle: 0,
            fontFunction: 'bold',
            textFunction: currentData.name,
            onHover,
            onMouseOut,
            onClick: () => {
            },
        });
    }

    // Draw items
    processedData.forEach((item, index) => {
      // Draw radial line
      const lineAngle = currentAngle - Math.PI / 2;

      const startX = rScale(1.5) * Math.cos(lineAngle);
      const startY = rScale(1.5) * Math.sin(lineAngle);
      const endX = rScale(8.5) * Math.cos(lineAngle);
      const endY = rScale(8.5) * Math.sin(lineAngle);
      const endArrowX = rScale(9) * Math.cos(lineAngle);
      const endArrowY = rScale(9) * Math.sin(lineAngle);
        
      // Handle categories and subcategories
      const subcategories = item.subcategories || [];
      const categorySlices = item.allocatedSlots;
      const categoryEndAngle = currentAngle + (categorySlices * angleSlice);
      const categoryWidth = categoryEndAngle - currentAngle;

      if (!isLeafItem(item)) {
        // Draw the line from the center to the main subcategory
        itemsGroup
          .append('line')
          .attr('x1', startX)
          .attr('y1', startY)
          .attr('x2', endX)
          .attr('y2', endY)
          .style('stroke', '#DDE6EE')
          .style('stroke-width', '1px');

        // Define arc generators
        const arcGenerator = d3.arc()
            .innerRadius(rScale(7.8))
            .outerRadius(rScale(8.2))
            .startAngle(currentAngle)
            .endAngle(categoryEndAngle);

        const interactionArcGenerator = d3.arc()
            .innerRadius(rScale(7.8))
            .outerRadius(rScale(8.2))
            .startAngle(currentAngle)
            .endAngle(categoryEndAngle);

        // Create a group for the arcs
        const arcGroup = itemsGroup
            .append('g')
            .attr('class', 'arc-group');

        // Add radial highlight area that becomes visible on hover
        const radialHighlight = arcGroup
            .append('path')
            .attr('d', d3.arc()
              .innerRadius(rScale(1.5))
              .outerRadius(rScale(8.2))
              .startAngle(currentAngle)
              .endAngle(categoryEndAngle)
            )
            .style('fill', item.color || '#4A90E2')
            .style('opacity', 0)
            .style('filter', 'blur(20px)')
            .style('transition', 'opacity 0.3s ease');

        // Add the blurred background arc that's always visible
        arcGroup
            .append('path')
            .attr('d', arcGenerator)
            .style('fill', item.color || '#4A90E2')
            .style('opacity', 0.3)
            .style('filter', 'blur(3px)');

        // Add the hover arc that becomes visible on hover
        const hoverArc = arcGroup
            .append('path')
            .attr('d', arcGenerator)
            .style('fill', item.color || '#4A90E2')
            .style('opacity', 0)
            .attr("class", "category-arc")
            .style('transition', 'opacity 0.2s ease');

        // The invisible interaction arc remains the same
        itemsGroup
            .append('path')
            .attr('d', interactionArcGenerator)
            .style('fill', 'transparent')
            .style('cursor', 'pointer')
            .on('mouseover', function(e) {
              hoverArc.style('opacity', 0.8);
              radialHighlight.style('opacity', 0.15);
              onHover(item, true, e);
            })
            .on('mouseout', function() {
              hoverArc.style('opacity', 0);
              radialHighlight.style('opacity', 0);
              onMouseOut();
            })
            .on('click', (e) => {
              e.stopPropagation();
              if (!isLeafItem(item)) {
                onSelection(item);
              }
            });

        // Draw the arrow
        drawArrow(itemsGroup, startX, startY, endArrowX, endArrowY);

        // Draw category label
        const labelRadius = isAtLeafLevel ? rScale(9) : rScale(9.5);
        drawText(itemsGroup, {
          item,
          position: {
            x: labelRadius * Math.cos(currentAngle - Math.PI / 2),
            y: labelRadius * Math.sin(currentAngle - Math.PI / 2)
          },
          color: COLORS.PRIMARY,
          currentAngle: currentAngle,
          fontFunction: 'bold',
          textFunction: item.name.toUpperCase(),
          onHover: (hoveredItem, isArc, e) => {
            hoverArc.style('opacity', 0.8);
            radialHighlight.style('opacity', 0.15);
            onHover(hoveredItem, isArc, e);
          },
          onMouseOut: () => {
            hoverArc.style('opacity', 0);
            radialHighlight.style('opacity', 0);
            onMouseOut();
          },
          onClick: (clickedItem) => {
            onSelection(clickedItem);
          }
        });

        // If there are hidden items, show the count
        if (item.hiddenCount > 0) {
          const hiddenLabel = `${item.hiddenCount} more items...`;
          const midAngle = currentAngle + (categoryWidth / 2);
          const labelRadius = rScale(5.6); // Position between dots (8.8) and category arc (8.2)
          
          itemsGroup
            .append('text')
            .attr('x', labelRadius * Math.cos(midAngle - Math.PI / 2))
            .attr('y', labelRadius * Math.sin(midAngle - Math.PI / 2))
            .attr('transform', `
              translate(${labelRadius * Math.cos(midAngle - Math.PI / 2)},
                       ${labelRadius * Math.sin(midAngle - Math.PI / 2)})
              rotate(${((midAngle * 180) / Math.PI - 90)})
              ${midAngle > Math.PI ? 'rotate(180)' : ''}
              translate(${-labelRadius * Math.cos(midAngle - Math.PI / 2)},
                       ${-labelRadius * Math.sin(midAngle - Math.PI / 2)})
            `)
            .style('text-anchor', 'middle')
            .style('font-size', '12px') // Slightly smaller font
            .style('font-style', 'italic')
            .style('fill', 'darkred')
            .style('opacity', 0.7)
            .text(hiddenLabel);
        }

        // Draw visible subcategories (limit to allocated slots)
        if (subcategories.length > 0) {
          const visibleSubcategories = subcategories.slice(0, item.allocatedSlots);
          const subItemSpacing = categoryWidth / Math.max(visibleSubcategories.length, 2); // Ensure minimum spacing even with 1 item
          
          visibleSubcategories.forEach((subcat, subIndex) => {
            // Adjust spacing to avoid center
            const subcatAngle = currentAngle + (subIndex + 0.5) * subItemSpacing; // Start from 0.5 to center items in their slots
            
            if (subcat.approvalStage) {
              drawLineAndDot(itemsGroup, {
                item: subcat,
                rScale,
                subcatAngle,
                onHover: (hoveredItem, isArc, e) => {
                  hoverArc.style('opacity', 0.8);
                  radialHighlight.style('opacity', 0.15);
                  onHover(hoveredItem, isArc, e);
                },
                onMouseOut: () => {
                  hoverArc.style('opacity', 0);
                  radialHighlight.style('opacity', 0);
                  onMouseOut();
                },
              });

              // Store the actual angle for this leaf item
              if (isLeafItem(subcat)) {
                leafItemAngles.set(subcat.id, subcatAngle);
              }
            }

            const subcatDistance = rScale(8.8);
            const normalizedAngle = ((subcatAngle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
            drawText(itemsGroup, {
              item: subcat,
              position: {
                x: subcatDistance * Math.cos(subcatAngle - Math.PI / 2),
                y: subcatDistance * Math.sin(subcatAngle - Math.PI / 2)
              },
              color: COLORS.SECONDARY,
              currentAngle: normalizedAngle,
              fontFunction: selectedLeafItem?.id === subcat.id || hoveredItem?.id === subcat.id ? 'bold' : 'normal',
              textFunction: subcat.name,
              onHover: (hoveredItem, isArc, e) => {
                hoverArc.style('opacity', 0.8);
                radialHighlight.style('opacity', 0.15);
                onHover(hoveredItem, isArc, e);
              },
              onMouseOut: () => {
                hoverArc.style('opacity', 0);
                radialHighlight.style('opacity', 0);
                onMouseOut();
              },
              onClick: isAtLeafLevel ? () => setSelectedLeafItem(subcat) : () => onSelection(subcat),
            });
          });
        }
      } else if (item.approvalStage) {
        // If it's a leaf item with approval stage, draw its line and dot
        const leafAngle = currentAngle + (categoryWidth / 2); // Center in its allocated space
        drawLineAndDot(itemsGroup, {
          item,
          rScale,
          subcatAngle: leafAngle,
          onHover,
          onMouseOut,
        });

        // Store the actual angle for this leaf item
        leafItemAngles.set(item.id, leafAngle);

        // Draw the leaf item label
        const subcatDistance = rScale(8.8);
        drawText(itemsGroup, {
          item,
          position: {
            x: subcatDistance * Math.cos(leafAngle - Math.PI / 2),
            y: subcatDistance * Math.sin(leafAngle - Math.PI / 2)
          },
          color: COLORS.SECONDARY,
          currentAngle: leafAngle,
          fontFunction: selectedLeafItem?.id === item.id || hoveredItem?.id === item.id ? 'bold' : 'normal',
          textFunction: item.name,
          onHover,
          onMouseOut,
          onClick: isAtLeafLevel ? () => setSelectedLeafItem(item) : undefined,
        });
      }

      currentAngle = categoryEndAngle;
    });

    // Draw chord connections if we're at leaf level
    if (isAtLeafLevel) {
      const connections = findItemsWithSharedTags(processedData);
      connections.forEach(({ source, target, sharedTags, color }) => {
        const sourceAngle = leafItemAngles.get(source.id);
        const targetAngle = leafItemAngles.get(target.id);
        
        drawChordConnection(connectionsGroup, {
          source,
          target,
          sourceAngle,
          targetAngle,
          rScale,
          sharedTags,
          color
        });
      });
    }

    // Draw back button last, in its own top layer
    if (selectedSubcategory) {
      renderBackButton(backButtonGroup, rScale, () => {
        setSelectedLeafItem(null);
        onBack();
      });

      // Add a white circle background behind the back button for better visibility
      backButtonGroup
        .insert('circle', ':first-child')
        .attr('r', rScale(1.2))
        .style('fill', 'white')
        .style('opacity', 0.9);
    }

  }, [data, selectedSubcategory, zoomFactor, onBack, selectedLeafItem, calculateCapacity, distributeCapacity]);

  return (
    <ChartContainer
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseOut}
      style={{
        cursor: isDragging ? 'grabbing' : zoomFactor > 1 ? 'grab' : 'default'
      }}
    >
      <svg ref={svgRef} />
    </ChartContainer>
  );
};

export default RadarChart;
